import { Field } from '@headlessui/react';
import { ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  GlobalError,
} from 'react-hook-form';
import { MultiSelect } from 'react-multi-select-component';
import { Label } from '@/components/form/label';
import { LabelAndValue } from '@/components/form/label-and-value';
import { SelectField, SelectFieldProps } from '@/components/form/select-field';
import { TextField } from '@/components/form/text-field';
import { FieldSchema } from '@/forms/types/field-schema';

export const renderFieldItem = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  key: TFieldName,
  schema: FieldSchema<TFieldValues, TFieldName>,
  control: Control<TFieldValues>,
): ReactNode => {
  switch (schema.type) {
    case 'readonly':
      return (
        <LabelAndValue key={key} label={schema.label} value={schema.value} />
      );
    case 'text':
      return (
        <Controller
          control={control}
          defaultValue={schema.value}
          key={key}
          name={key}
          render={({ field, formState: { errors } }) => (
            <TextField
              {...field}
              error={errors[key] as GlobalError}
              label={schema.label}
              placeholder={schema.placeholder}
            />
          )}
          rules={schema.validation}
        />
      );
    case 'select':
      return (
        <Controller
          control={control}
          defaultValue={schema.value}
          key={key}
          name={key}
          render={({
            field: { onChange, ...field },
            formState: { errors },
          }) => {
            const isNumber = typeof schema.options?.[0]?.value === 'number';

            const handleChange: SelectFieldProps['onChange'] = (event) => {
              const { value } = event.target;
              const isEmpty = value === '';
              if (isEmpty) {
                onChange(value);
              } else {
                onChange(isNumber ? +value : value);
              }
            };

            return (
              <SelectField
                {...field}
                error={errors[key] as GlobalError}
                label={schema.label}
                onChange={handleChange}
                options={schema.options ?? []}
                placeholder={schema.placeholder}
              />
            );
          }}
          rules={schema.validation}
        />
      );
    case 'multiselect':
      return (
        <Controller
          control={control}
          defaultValue={schema.value}
          key={key}
          name={key}
          render={({ field: { onChange, value } }) => (
            <Field>
              <div className="mb-2 flex flex-col">
                <Label>{schema.label}</Label>
              </div>
              <MultiSelect
                disableSearch
                labelledBy="Select"
                onChange={onChange}
                options={schema.options ?? []}
                value={value}
              />
            </Field>
          )}
          rules={schema.validation}
        />
      );
    default:
      return null;
  }
};
